// src/Pages/Account/DeleteAccount.js
import React, { useState } from "react";
import {
  ThemeProvider,
  CssBaseline,
  Toolbar,
  Box,
  Typography,
  Button,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useMutation } from "react-query";

import client from "../../API";
import routes from "../../Config/routes";
import HomeHeader from "../../Components/HomeHeader";
import { lightTheme } from "../../Utils/baseTheme";
import useAuth from "../../Hooks/use-auth";
import { useCreatorAuth } from "../../Hooks/creator-use-auth";

const DeleteAccount = () => {
  const navigate = useNavigate();
  const { unauthorize } = useAuth();               // brand user logout
  const { logout: creatorLogout } = useCreatorAuth(); // creator logout

  /* ───── local state ───── */
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "" });

  /* ───── API call ───── */
  const deleteMutation = useMutation(() => client.users.deleteAccount());

  const clearCredsAndGoHome = (message) => {
    unauthorize();          // normal users
    creatorLogout?.();      // creators (no-op for normal)
    setSnack({ open: true, msg: message });
    navigate(routes.home);
  };

  const handleDelete = async () => {
    setLoading(true);
    let ok = false;

    try {
      await deleteMutation.mutateAsync();
      ok = true;                          // 204/200 path
    } catch (err) {
      console.error("delete-account error:", err);
    } finally {
      setLoading(false);
      setConfirmOpen(false);
      clearCredsAndGoHome(
        ok
          ? "Your account has been deleted."
          : "Your account deletion has begun processing. It may take up to 24 hours to clear all data properly."
      );
    }
  };

  /* ───── render ───── */
  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <HomeHeader />
      <Toolbar />

      <Backdrop
        sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* —— confirmation dialog —— */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Account?</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            This will <b>permanently remove</b> your account and personal data.
            You can’t undo this action.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* —— snackbar feedback —— */}
      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.msg}
      />

      <Box sx={{ p: 2, maxWidth: "32em", mx: "auto" }}>
        <Typography variant="h3" gutterBottom>
          Delete Account
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          We’re sorry to see you go! Deleting your account removes your
          personal data from Blitz. Transaction records required for financial
          compliance are retained in anonymised form.
        </Typography>

        <Button
          variant="contained"
          color="error"
          sx={{ mt: 3 }}
          onClick={() => setConfirmOpen(true)}
        >
          Delete My Account
        </Button>
      </Box>
    </ThemeProvider>
  );
};

export default DeleteAccount;
